import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { CamionMuelle, CargaSalida, SlotMuelle } from '@/types'
import { Loader } from '@/components/common/Loader'
import { errorMuelleDe, useDockMutaciones, validarTramoEnCliente, type ErrorMuelle } from '@/hooks/useDockMutaciones'
import { cuerpoCamion, hhmm, paletsDeCamion } from '@/utils/camionesMuelle'

/** `Date` → el `value` de un `<input type="datetime-local">`, en hora local. */
function aLocalInput(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

/** El `value` del input → ISO **con zona**.
 *
 *  `new Date('2026-09-23T08:00')` se interpreta en hora local, y `toISOString()`
 *  le pone el offset. Mandar la cadena tal cual —sin zona— haría que el backend
 *  la tomara por UTC y el camión se movería dos horas. */
function aISOConZona(valor: string): string | null {
  if (!valor) return null
  const d = new Date(valor)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/**
 * Alta y edición de un camión, y sus cargas.
 *
 * **Sin estado optimista**: el botón se queda en `isPending` hasta que el
 * servidor contesta. Él es la autoridad sobre dos cosas que el cliente no sabe —
 * si el muelle se ha solapado y si otro usuario se ha quedado la carga—, y
 * pintar el camión para retirarlo trescientos milisegundos después por un 409 es
 * peor que esperar.
 *
 * Y en un rechazo **el drawer no se cierra y no se pierde lo tecleado**: cerrarlo
 * obligaría a escribirlo todo otra vez para cambiar una hora.
 */
export function CamionDrawer({
  abierto, camion, slotPorDefecto, cuandoPorDefecto, cargaInicial, almacen, slots,
  cargasDisponibles, cargasDelCamion, jornada, dark, onCerrar,
}: {
  abierto: boolean
  /** `null` = alta. */
  camion: CamionMuelle | null
  slotPorDefecto?: string
  cuandoPorDefecto?: Date
  /** Carga que se acaba de soltar en un hueco: se engancha en cuanto el
   *  camión existe, para que el arrastre no acabe a medias. */
  cargaInicial?: string
  almacen: string
  slots: SlotMuelle[]
  cargasDisponibles: CargaSalida[]
  cargasDelCamion: CargaSalida[]
  jornada: { inicio: number; fin: number }
  dark: boolean
  onCerrar: () => void
}) {
  const { t } = useTranslation()
  const m = useDockMutaciones(almacen)

  const [muelle, setMuelle] = useState('')
  const [inicio, setInicio] = useState('')
  const [fin, setFin] = useState('')
  const [matricula, setMatricula] = useState('')
  const [transportista, setTransportista] = useState('')
  const [palets, setPalets] = useState('')
  const [error, setError] = useState<ErrorMuelle | null>(null)

  useEffect(() => {
    if (!abierto) return
    setError(null)
    if (camion) {
      setMuelle(camion.muelle ?? '')
      setInicio(aLocalInput(camion.fechaInicio))
      setFin(aLocalInput(camion.fechaFin))
      setMatricula(camion.matricula ?? '')
      setTransportista(camion.transportista ?? '')
      setPalets(camion.palets != null ? String(camion.palets) : '')
    } else {
      const arranque = cuandoPorDefecto ?? new Date()
      const dos = new Date(arranque.getTime() + 2 * 3_600_000)
      setMuelle(slotPorDefecto ?? slots[0]?.nombre ?? '')
      setInicio(aLocalInput(arranque.toISOString()))
      setFin(aLocalInput(dos.toISOString()))
      setMatricula(''); setTransportista(''); setPalets('')
    }
  }, [abierto, camion, slotPorDefecto, cuandoPorDefecto, slots])

  if (!abierto) return null

  const guardando = m.crearCamion.isPending || m.actualizarCamion.isPending
  // La suma estimada de las cargas enganchadas. Es lo que se pinta si nadie
  // teclea nada, y lo que se ofrece como marca de agua del campo.
  // Se calcula IGNORANDO el campo manual: si no, la ayuda repetiría el número
  // que el usuario acaba de teclear en vez de ofrecerle la alternativa.
  const estimados = paletsDeCamion(
    { ...(camion ?? { id: '', activo: true, cargas: [] }), palets: null },
    cargasDelCamion,
  )
  const bdr = dark ? 'border-white/10' : 'border-slate-200'
  const campo = `w-full px-2 py-1.5 text-[12px] rounded border ${
    dark ? 'bg-slate-800 border-white/10 text-slate-100' : 'bg-white border-slate-300 text-slate-900'
  }`
  const etiqueta = `block text-[10px] font-bold uppercase tracking-wider mb-1 ${
    dark ? 'text-slate-500' : 'text-slate-400'
  }`

  const guardar = () => {
    setError(null)
    const iniISO = aISOConZona(inicio)
    const finISO = aISOConZona(fin)
    if (!muelle || !iniISO || !finISO) {
      setError({ codigo: 'faltan_campos' })
      return
    }
    // Las mismas validaciones que el backend, con los MISMOS códigos: así el
    // mensaje es idéntico venga de donde venga, y la mayoría no llega a viajar.
    const fallo = validarTramoEnCliente(new Date(iniISO), new Date(finISO), jornada)
    if (fallo) { setError(fallo); return }

    // Los nombres del cuerpo son los de la API, no los que suenen mejor aquí:
    // `cuerpoCamion` es el único sitio donde se deciden, y tiene test.
    const body = cuerpoCamion({
      muelle, almacen,
      inicioLocal: inicio, finLocal: fin,
      matricula, transportista, palets,
    })
    if (!body) { setError({ codigo: 'faltan_campos' }); return }
    const alFallar = (e: unknown) => setError(errorMuelleDe(e))
    if (camion) {
      m.actualizarCamion.mutate({ id: camion.id, body }, { onSuccess: onCerrar, onError: alFallar })
      return
    }
    m.crearCamion.mutate(body, {
      onError: alFallar,
      onSuccess: (r) => {
        // Si el camión nació de soltar una carga, se le engancha aquí: dejar el
        // arrastre a medias obligaría al usuario a repetir lo que ya hizo.
        if (cargaInicial) {
          m.asignarCarga.mutate(
            { camionId: r.data.id, carga: cargaInicial },
            { onSuccess: onCerrar, onError: alFallar },
          )
        } else {
          onCerrar()
        }
      },
    })
  }

  const borrar = () => {
    if (!camion) return
    m.borrarCamion.mutate(camion.id, {
      onSuccess: onCerrar,
      onError: (e) => setError(errorMuelleDe(e)),
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={guardando ? undefined : onCerrar} />
      <div className={`relative w-full max-w-md h-full overflow-y-auto shadow-xl ${dark ? 'bg-slate-900' : 'bg-white'}`}>
        <header className={`sticky top-0 px-4 py-3 border-b ${bdr} ${dark ? 'bg-slate-900' : 'bg-white'}`}>
          <h2 className={`text-[14px] font-black ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
            {camion ? t('muelles.drawer.editar', { codigo: camion.codigo ?? '' }) : t('muelles.drawer.nuevo')}
          </h2>
          {camion && (
            <p className={`text-[11px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
              {hhmm(camion.fechaInicio)}–{hhmm(camion.fechaFin)} · {camion.muelle}
            </p>
          )}
        </header>

        <div className="p-4 space-y-3">
          <div>
            <label className={etiqueta} htmlFor="cam-muelle">{t('muelles.drawer.muelle')}</label>
            <select id="cam-muelle" className={campo} value={muelle} onChange={(e) => setMuelle(e.target.value)}>
              {slots.length === 0 && <option value="">{t('muelles.drawer.sinSlots')}</option>}
              {slots.map((s) => <option key={s.nombre} value={s.nombre}>{s.nombre}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={etiqueta} htmlFor="cam-ini">{t('muelles.drawer.inicio')}</label>
              <input id="cam-ini" type="datetime-local" className={campo} value={inicio} onChange={(e) => setInicio(e.target.value)} />
            </div>
            <div>
              <label className={etiqueta} htmlFor="cam-fin">{t('muelles.drawer.fin')}</label>
              <input id="cam-fin" type="datetime-local" className={campo} value={fin} onChange={(e) => setFin(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={etiqueta} htmlFor="cam-mat">{t('muelles.drawer.matricula')}</label>
              <input id="cam-mat" className={campo} value={matricula} onChange={(e) => setMatricula(e.target.value)} />
            </div>
            <div>
              <label className={etiqueta} htmlFor="cam-tr">{t('muelles.drawer.transportista')}</label>
              <input id="cam-tr" className={campo} value={transportista} onChange={(e) => setTransportista(e.target.value)} />
            </div>
          </div>

          <div>
            <label className={etiqueta} htmlFor="cam-pal">{t('muelles.drawer.palets')}</label>
            <input
              id="cam-pal" type="number" min={0} className={campo}
              value={palets}
              onChange={(e) => setPalets(e.target.value)}
              placeholder={estimados.total !== null ? String(estimados.total) : undefined}
            />
            {/* Lo normal es dejarlo vacío: se suma solo desde las cargas. El campo
                está para cuando el almacén cuenta y sale otra cosa. */}
            <p className={`text-[10px] mt-1 ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
              {estimados.total === null
                ? t('muelles.drawer.paletsSinEstimar')
                : estimados.incompleto > 0
                  ? t('muelles.drawer.paletsEstimadoParcial', { n: estimados.total, cargas: estimados.incompleto })
                  : t('muelles.drawer.paletsEstimado', { n: estimados.total })}
            </p>
          </div>

          {error && (
            <div className={`px-3 py-2 rounded text-[11px] ${dark ? 'bg-rose-950/50 text-rose-300' : 'bg-rose-50 text-rose-700'}`}>
              {t(`muelles.errores.${error.codigo}`, {
                defaultValue: t('muelles.errores.error_inesperado'),
                ...(error.valores ?? {}),
                camion: error.camion ?? '',
                carga: error.carga ?? '',
              })}
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              disabled={guardando}
              onClick={guardar}
              className="px-3 py-1.5 text-[12px] font-semibold rounded bg-sky-500 text-white disabled:opacity-60"
            >
              {t('muelles.drawer.guardar')}
            </button>
            <button
              type="button"
              disabled={guardando}
              onClick={onCerrar}
              className={`px-3 py-1.5 text-[12px] rounded ${dark ? 'text-slate-300 hover:bg-white/5' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              {t('common.cancel', { defaultValue: 'Cancelar' })}
            </button>
            {guardando && <Loader size={9} />}
            {camion && (
              <button
                type="button"
                disabled={m.borrarCamion.isPending}
                onClick={borrar}
                className="ml-auto px-3 py-1.5 text-[12px] font-semibold rounded text-rose-500 hover:bg-rose-500/10 disabled:opacity-60"
                title={t('muelles.drawer.borrarTooltip')}
              >
                {t('muelles.drawer.borrar')}
              </button>
            )}
          </div>
        </div>

        {/* ── Cargas enganchadas ─────────────────────────────────────────── */}
        {camion && (
          <section className={`border-t ${bdr} p-4`}>
            <h3 className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
              {t('muelles.drawer.cargas')}
            </h3>

            {cargasDelCamion.length === 0 ? (
              // Es el caso NORMAL a primera hora: un camión se reserva antes de
              // saber qué se le mete.
              <p className={`text-[11px] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                {t('muelles.drawer.sinCargas')}
              </p>
            ) : (
              <ul className="space-y-1 mb-3">
                {cargasDelCamion.map((c) => (
                  <li key={c.carga} className="flex items-center justify-between gap-2">
                    <span className={`text-[11px] font-mono ${dark ? 'text-slate-200' : 'text-slate-700'}`}>{c.carga}</span>
                    <button
                      type="button"
                      disabled={m.quitarCarga.isPending}
                      onClick={() => m.quitarCarga.mutate(
                        { camionId: camion.id, carga: c.carga },
                        { onError: (e) => setError(errorMuelleDe(e)) },
                      )}
                      className="text-[10px] text-rose-500 hover:underline disabled:opacity-60"
                    >
                      {t('muelles.drawer.quitar')}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <label className={etiqueta} htmlFor="cam-add">{t('muelles.drawer.anadirCarga')}</label>
            <select
              id="cam-add"
              className={campo}
              value=""
              disabled={m.asignarCarga.isPending}
              onChange={(e) => {
                if (!e.target.value) return
                m.asignarCarga.mutate(
                  { camionId: camion.id, carga: e.target.value },
                  { onError: (err) => setError(errorMuelleDe(err)) },
                )
              }}
            >
              <option value="">{t('muelles.drawer.elegirCarga')}</option>
              {cargasDisponibles.map((c) => (
                <option key={c.carga} value={c.carga}>
                  {c.carga} · {c.destino ?? c.cliente ?? ''}
                </option>
              ))}
            </select>
            {m.asignarCarga.isPending && <Loader size={9} />}
          </section>
        )}
      </div>
    </div>
  )
}
